/**
 * Typed client for the opencode headless server (`opencode serve`).
 *
 * ENG-9096: this client speaks the `/session` surface. It used to speak `/api`,
 * and that was the whole of opencode#38470. `/api/*` routes go through workspace
 * routing (`middleware/workspace-routing.ts` -> `selectedV2WorkspaceID`) and
 * resolve a PER-REQUEST directory, so an API-driven session ran in an instance
 * that had never loaded the configured MCP servers. `/session` does not.
 *
 * Both halves of the old path were broken by that, which is why the workaround
 * (ENG-8032) had to leave HTTP entirely and spawn `opencode run --attach`:
 *
 *   POST /api/session/{id}/prompt   -> ran the turn with NO MCP tools
 *   GET  /api/session/{id}/message  -> returned `{"data":[]}` for that session,
 *                                      so `waitIdle`'s poll never saw a reply
 *
 * Measured on 1.18.3 (the pinned `OPENCODE_NPM_VERSION`), same session id, same
 * server process, same instant: `/session/{id}/message` returned both messages
 * while `/api/session/{id}/message` returned an empty array.
 *
 * Routes now used - note these return values BARE, where `/api` wrapped
 * everything in `{data: ...}`:
 *
 *   POST /session                  -> { id, title, time, ... }
 *   POST /session/{id}/message     -> { info: { id, role, parentID, ... }, parts: [...] }
 *   GET  /session/{id}/message     -> [ { info, parts }, ... ]   (oldest-first)
 *   GET  /session                  -> [ { id, title, time, ... }, ... ]
 *
 * `POST /session/{id}/message` BLOCKS until the turn completes and returns the
 * assistant message, so the former prompt -> waitIdle -> latestAssistantText
 * sequence collapses into one call and the idle poll is gone. The server also
 * queues overlapping turns on a session natively, which is what `delivery:
 * 'queue'` bought on the old path - so that parameter has no equivalent and is
 * not needed. It has no `steer` equivalent either; nothing used steer.
 *
 * The server enforces HTTP Basic auth (`OPENCODE_SERVER_PASSWORD`), handled via
 * the `password` option below.
 *
 * The HTTP layer is injectable (`fetchImpl`) so the bridge is unit-testable
 * without a live server. `fetch` is accessed via a locally-declared signature
 * so this compiles without the DOM lib.
 */

/** Minimal fetch surface — avoids a DOM-lib dependency. */
export interface MinimalFetchResponse {
  ok: boolean;
  status: number;
  text(): Promise<string>;
}
export type MinimalFetch = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal },
) => Promise<MinimalFetchResponse>;

export interface OpencodeClientOptions {
  /** Base URL of the opencode server, e.g. http://127.0.0.1:4599 */
  baseUrl: string;
  /** OPENCODE_SERVER_PASSWORD, if the server was started with one (basic auth). */
  password?: string;
  /** Basic-auth username; opencode defaults to `opencode`. */
  username?: string;
  /** Injected fetch (defaults to globalThis.fetch). */
  fetchImpl?: MinimalFetch;
  /** Per-request timeout (ms) enforced via AbortController. Default 120000. */
  requestTimeoutMs?: number;
}

/**
 * The model selector opencode's HTTP API expects on session-create and prompt.
 * NOTE (ENG-7931): the field is `id` (the bare model id, e.g. `grok-4.5`), NOT
 * `modelID` - opencode rejects the latter with `schema rejection … Missing key
 * ["model"]["id"]` (HTTP 400). `providerID` is the config provider id (e.g. the
 * xAI-compat `grok`).
 */
export interface OpencodeModelRef {
  providerID: string;
  id: string;
}

export interface CreateSessionParams {
  agent?: string;
  model?: OpencodeModelRef;
  title?: string;
}

export interface SendMessageParams {
  sessionID: string;
  text: string;
  /**
   * The model to run this turn on. Optional here, unlike on the old `/api`
   * path: measured on 1.18.3, `/session` DOES apply opencode.json's default
   * `model` (a turn sent with no `model` ran `claude-sonnet-5`, the configured
   * default). ENG-7931's mandatory-explicit-model workaround was specific to
   * `/api`, which fell back to a free Zen model. Still passed explicitly by the
   * manager so the resolved model is deterministic rather than config-derived.
   */
  model?: OpencodeModelRef;
}

/** The outcome of one completed turn (`POST /session/{id}/message`). */
export interface SentTurn {
  /** Concatenated text of the assistant's reply, or null if it produced none. */
  reply: string | null;
  /** id of the assistant message this turn produced. */
  messageID?: string;
  /**
   * id of the USER message this reply answers. Note this is the only turn
   * identity the response carries - it does NOT let a caller confirm the reply
   * belongs to the turn it submitted, because the caller is never told its own
   * user-message id. That is why turns are serialised per session; see the
   * note on `OpencodeInboundBridge`.
   */
  parentID?: string;
}

/**
 * One content part of a session message. opencode assistant turns interleave
 * `text`, `reasoning`, and `tool` parts; `state` carries the tool call's live
 * status (`running` while a tool is mid-flight, `completed`/`error` after) plus
 * an optional human `title` (e.g. `echo hello`). Only text/reasoning carry
 * `text`; tool parts carry `tool` (the tool name) + `state`.
 */
export interface SessionMessagePart {
  type?: string;
  text?: string;
  tool?: string;
  state?: { status?: string; title?: string; error?: string };
}

/** Subset of an opencode session message this client reads (ENG-7927 widened it to tool/reasoning parts). */
export interface SessionMessage {
  id?: string;
  type?: string;
  time?: { created?: number; completed?: number };
  finish?: string;
  content?: SessionMessagePart[];
}

/**
 * One message as `GET /session/{id}/message` returns it: identity/metadata under
 * `info`, content under `parts`. Distinct from the internal {@link SessionMessage},
 * which keeps the flatter shape the old `/api` route used so downstream readers
 * did not have to change.
 */
export interface SessionMessageWire {
  info?: {
    id?: string;
    role?: string;
    parentID?: string;
    time?: { created?: number; completed?: number };
    finish?: string;
  };
  parts?: SessionMessagePart[];
}

/** Summary row from `GET /session` (the session list). */
export interface SessionSummary {
  id: string;
  title?: string;
  /** Epoch ms of last update / creation, used to pick the most recent session. */
  updated?: number;
  created?: number;
}

/** The subset of the opencode server the inbound rail depends on. */
export interface OpencodeClient {
  createSession(params?: CreateSessionParams): Promise<{ sessionID: string }>;
  /**
   * Run ONE user turn to completion and return the assistant's reply (ENG-9096).
   * This single call replaces the former prompt() + waitIdle() + latestAssistantText()
   * trio: `POST /session/{id}/message` blocks until the turn finishes and returns
   * the completed assistant message in its response body.
   */
  sendMessage(params: SendMessageParams): Promise<SentTurn>;
  /** All sessions the server knows about (newest-first is not guaranteed). ENG-7927. */
  listSessions(): Promise<SessionSummary[]>;
  /** Raw structured messages for a session (text + reasoning + tool parts). ENG-7927. */
  getStructuredMessages(sessionID: string): Promise<SessionMessage[]>;
}

export class HttpOpencodeClient implements OpencodeClient {
  private readonly base: string;
  private readonly headers: Record<string, string>;
  private readonly fetchImpl: MinimalFetch;
  private readonly requestTimeoutMs: number;

  constructor(opts: OpencodeClientOptions) {
    this.base = opts.baseUrl.replace(/\/$/, '');
    this.headers = { 'Content-Type': 'application/json' };
    if (opts.password) {
      const user = opts.username ?? 'opencode';
      const token = Buffer.from(`${user}:${opts.password}`).toString('base64');
      this.headers['Authorization'] = `Basic ${token}`;
    }
    const f = opts.fetchImpl ?? (globalThis as { fetch?: MinimalFetch }).fetch;
    if (!f) throw new Error('No fetch implementation available (pass fetchImpl).');
    this.fetchImpl = f;
    this.requestTimeoutMs = opts.requestTimeoutMs ?? 120_000;
  }

  private async call(method: string, path: string, body?: unknown): Promise<unknown> {
    // Bound every request so a hung opencode server can't wedge the poll loop /
    // handleInbound / watcher forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.requestTimeoutMs);
    let res: MinimalFetchResponse;
    try {
      res = await this.fetchImpl(`${this.base}${path}`, {
        method,
        headers: this.headers,
        signal: controller.signal,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } finally {
      clearTimeout(timer);
    }
    const raw = await res.text();
    if (!res.ok) {
      throw new Error(`opencode ${method} ${path} → HTTP ${res.status}: ${raw.slice(0, 300)}`);
    }
    if (!raw) return {};
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      throw new Error(`opencode ${method} ${path} → non-JSON response: ${raw.slice(0, 300)}`);
    }
  }

  async createSession(params?: CreateSessionParams): Promise<{ sessionID: string }> {
    // `/session` returns the session object BARE; `/api/session` wrapped it in
    // `{data: …}`. Reading `out.data.id` here would silently yield undefined.
    const out = (await this.call('POST', '/session', params ?? {})) as { id?: string };
    const id = out.id;
    if (!id) throw new Error('opencode createSession returned no session id');
    return { sessionID: id };
  }

  async sendMessage(params: SendMessageParams): Promise<SentTurn> {
    // This call BLOCKS for the whole turn and returns the completed assistant
    // message, so there is no admit-now/read-later split and no idle poll.
    //
    // The model ref is REMAPPED, not passed through: `/session` requires
    // `{providerID, modelID}` and rejects our `OpencodeModelRef`'s `id` with
    // `400 BadRequest: Missing key at ["model"]["modelID"]`. Measured, and it
    // fails loud rather than silently ignoring the model.
    const out = (await this.call('POST', `/session/${params.sessionID}/message`, {
      parts: [{ type: 'text', text: params.text }],
      ...(params.model
        ? { model: { providerID: params.model.providerID, modelID: params.model.id } }
        : {}),
    })) as { info?: { id?: string; parentID?: string }; parts?: SessionMessagePart[] };
    const reply = HttpOpencodeClient.textOf(out.parts ?? []);
    return { reply, messageID: out.info?.id, parentID: out.info?.parentID };
  }

  /** Concatenated text of the `text` parts of one message, or null if none. */
  private static textOf(parts: SessionMessagePart[]): string | null {
    const text = parts
      .filter((p) => p.type === 'text' && typeof p.text === 'string')
      .map((p) => p.text)
      .join('');
    return text || null;
  }

  private async fetchMessages(sessionID: string): Promise<SessionMessage[]> {
    // `/session/{id}/message` returns a BARE array of `{info, parts}`; the old
    // `/api` route returned `{data, cursor}` — and, on a session driven through
    // `/session`, returned `{"data":[]}` regardless of how many messages the
    // session actually held (opencode#38470; ENG-9096). Mapped to the internal
    // `{id, type, time, content}` shape so every downstream reader (transcript,
    // tool-call extraction) is unchanged: `info.role` becomes `type` and
    // `parts` becomes `content`.
    const out = (await this.call('GET', `/session/${sessionID}/message`)) as SessionMessageWire[];
    if (!Array.isArray(out)) return [];
    return out.map((m) => ({
      id: m.info?.id,
      type: m.info?.role,
      time: m.info?.time,
      finish: m.info?.finish,
      content: m.parts ?? [],
    }));
  }

  async listSessions(): Promise<SessionSummary[]> {
    // Bare array on `/session`, not `{data: […]}`.
    const out = (await this.call('GET', '/session')) as Array<{
      id?: string;
      title?: string;
      time?: { updated?: number; created?: number };
    }>;
    return (Array.isArray(out) ? out : [])
      .filter((s): s is { id: string; title?: string; time?: { updated?: number; created?: number } } =>
        typeof s.id === 'string',
      )
      .map((s) => ({ id: s.id, title: s.title, updated: s.time?.updated, created: s.time?.created }));
  }

  async getStructuredMessages(sessionID: string): Promise<SessionMessage[]> {
    return this.fetchMessages(sessionID);
  }

}
